import { useCallback, useEffect, useState } from "react";
import {
  ArrowRight,
  Blocks,
  Bot,
  CalendarClock,
  ChevronDown,
  Database,
  Gift,
  Globe,
  MessagesSquare,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  UsersRound,
  Wallet,
  Wand2,
  type LucideIcon,
} from "lucide-react";

import {
  changeSubscription,
  getBillingAccount,
  getBillingPlans,
  getMe,
  openBillingPortal,
  redeemCode,
  startCheckout,
  type BillingAccount,
  type CatalogPlan,
} from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/ui/toast";
import { PageHeader, Spinner } from "@/components/Page";

const POPULAR = "pro";

const euro = (cents: number) =>
  `€${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: cents % 100 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
const count = (v: number, unit: string) =>
  v === -1 ? `Unlimited ${unit}s` : `${v.toLocaleString()} ${unit}${v === 1 ? "" : "s"}`;

function integrationsLine(p: CatalogPlan): string {
  const kinds = p.stdio_openapi_integrations ? "MCP, HTTP API & stdio/npx" : "MCP";
  const n = p.connected_servers === -1 ? "Unlimited" : p.connected_servers;
  return `${n} connected integrations (${kinds})`;
}
function schedulingLine(p: CatalogPlan): string {
  return p.scheduled_tasks === -1
    ? "Unlimited scheduled & recurring tasks"
    : `${p.scheduled_tasks}× scheduled & recurring tasks`;
}

type Feature = { icon: LucideIcon; label: string };
function planFeatures(p: CatalogPlan): Feature[] {
  const f: Feature[] = [];
  if (p.trial_days > 0) f.push({ icon: Gift, label: `${p.trial_days}-day free trial` });
  f.push(
    { icon: RefreshCw, label: `+${p.daily_refresh_credits.toLocaleString()} credits / day` },
    { icon: Bot, label: "Autonomous AI agents" },
    { icon: MessagesSquare, label: "Chat, Telegram & Slack channels" },
    { icon: Wand2, label: "Build agents with AI" },
    { icon: Database, label: "Per-agent memory & storage" },
    { icon: Users, label: count(p.seats, "seat") },
    { icon: Blocks, label: integrationsLine(p) },
  );
  if (p.scheduling) f.push({ icon: CalendarClock, label: schedulingLine(p) });
  if (p.concurrent_missions !== 0)
    f.push({
      icon: Target,
      label:
        p.concurrent_missions === -1
          ? "Unlimited autonomous missions"
          : `${p.concurrent_missions} concurrent autonomous mission${p.concurrent_missions === 1 ? "" : "s"}`,
    });
  if (p.custom_domain) f.push({ icon: Globe, label: "Custom domains" });
  if (p.per_agent_budgets) f.push({ icon: Wallet, label: "Per-agent budget caps" });
  if (p.policy_enforce) f.push({ icon: ShieldCheck, label: "Policy enforcement & approvals" });
  if (p.advanced_rbac) f.push({ icon: UsersRound, label: "Advanced per-project roles" });
  return f;
}

function PlanCard({
  plan,
  annual,
  current,
  busy,
  canBuy,
  ctaLabel,
  onChoose,
}: {
  plan: CatalogPlan;
  annual: boolean;
  current: boolean;
  busy: boolean;
  canBuy: boolean;
  ctaLabel: string;
  onChoose: (credits: number) => void;
}) {
  const popular = plan.id === POPULAR;
  const tiers = plan.tiers ?? [];
  const [tierIdx, setTierIdx] = useState(0);
  const tier = tiers[tierIdx];
  const credits = tier ? tier.credits : plan.monthly_credits;
  const annualTotal = tier ? tier.annual_price_eur_cents : 0;
  const monthly = tier ? tier.price_eur_cents : plan.price_eur_cents;
  const shownMonthly = annual ? Math.round(annualTotal / 12) : monthly;
  const purchasable = annual
    ? tier
      ? tier.annual_purchasable
      : plan.annual_purchasable
    : tier
      ? tier.purchasable
      : plan.purchasable;

  return (
    <Card className={"relative flex flex-col " + (popular ? "border-primary shadow-lg ring-1 ring-primary/40" : "")}>
      {popular && (
        <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 gap-1">
          <Sparkles className="size-3" /> Most popular
        </Badge>
      )}
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base font-semibold text-muted-foreground">{plan.name}</CardTitle>
          {current && (
            <span className="rounded-full border border-primary/40 px-1.5 py-0.5 text-[10px] font-medium text-primary">
              Current
            </span>
          )}
        </div>
        <div className="mt-0.5 flex items-baseline gap-1">
          <span className="text-3xl font-semibold tracking-tight text-foreground">{euro(shownMonthly)}</span>
          <span className="text-sm text-muted-foreground">/ month</span>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {annual ? `billed annually · ${euro(annualTotal)}/yr` : "billed monthly"}
        </p>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        <Button
          size="lg"
          className="w-full rounded-full font-semibold"
          variant={popular ? "default" : "secondary"}
          disabled={!purchasable || !canBuy || busy}
          onClick={() => onChoose(credits)}
        >
          {busy ? <Spinner /> : null}
          {purchasable ? ctaLabel : "Unavailable"}
        </Button>

        {tiers.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex w-full items-center justify-between rounded-lg bg-muted px-3.5 py-2.5 text-sm font-medium transition-colors hover:bg-muted/70">
                <span>{credits.toLocaleString()} credits / month</span>
                <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
              {tiers.map((t, i) => (
                <DropdownMenuItem key={t.credits} onSelect={() => setTierIdx(i)}>
                  {t.credits.toLocaleString()} credits / month
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="flex w-full items-center rounded-lg bg-muted px-3.5 py-2.5 text-sm font-medium">
            {credits.toLocaleString()} credits / month
          </div>
        )}

        <ul className="flex-1 space-y-2.5 text-sm">
          {planFeatures(plan).map((f) => (
            <li key={f.label} className="flex items-start gap-2.5">
              <f.icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <span>{f.label}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export function Billing() {
  const toast = useToast();
  const [plans, setPlans] = useState<CatalogPlan[] | null>(null);
  const [acct, setAcct] = useState<BillingAccount | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [cycle, setCycle] = useState<"month" | "year">("month");
  const [busyPlan, setBusyPlan] = useState("");
  const [err, setErr] = useState("");
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [redeemErr, setRedeemErr] = useState("");
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [managing, setManaging] = useState(false);

  const loadAcct = useCallback(() => {
    getBillingAccount().then(setAcct).catch(() => setAcct(null));
  }, []);

  useEffect(() => {
    getBillingPlans()
      .then((r) => setPlans(r.plans))
      .catch((e) => setErr(humanizeError(e, "Couldn't load the plans.")));
    loadAcct();
    getMe()
      .then((me) => {
        const role = me.workspaces?.find((w) => w.tenant_id === me.current_tenant_id)?.role;
        setIsOwner(me.auth === "api_key" || role === "owner");
      })
      .catch(() => setIsOwner(false));
  }, [loadAcct]);

  const annual = cycle === "year";
  const subscribed = !!acct?.stripe_managed;

  async function choose(planId: string, credits: number) {
    setErr("");
    setBusyPlan(planId);
    try {
      if (!subscribed) {
        // No active subscription → start a fresh one via Checkout.
        const { url } = await startCheckout(planId, credits, cycle);
        window.location.assign(url);
        return;
      }
      // Existing subscriber → change in place (upgrade now / downgrade at period end).
      const res = await changeSubscription(planId, credits, cycle);
      if (res.effect === "immediate") {
        toast.success("Plan updated — your new plan is active now.");
      } else if (res.effect === "period_end") {
        const when = res.effective_at ? new Date(res.effective_at * 1000).toLocaleDateString() : "your next renewal";
        toast.success(`Change scheduled — it takes effect on ${when}. You keep your current plan until then.`);
      } else {
        toast.success("You're already on this plan.");
      }
      loadAcct();
      setBusyPlan("");
    } catch (e) {
      setErr(humanizeError(e, "We couldn't change your plan. Please try again."));
      setBusyPlan("");
    }
  }

  async function applyCode() {
    const c = code.trim();
    if (!c || redeeming) return;
    setRedeemErr("");
    setRedeeming(true);
    try {
      await redeemCode(c);
      window.location.assign("/credits");
    } catch (e) {
      setRedeemErr(humanizeError(e, "We couldn't apply that code."));
      setRedeeming(false);
    }
  }

  async function manageSubscription() {
    setManaging(true);
    try {
      const { url } = await openBillingPortal();
      window.location.assign(url);
    } catch (e) {
      toast.error(humanizeError(e, "Couldn't open billing management."));
      setManaging(false);
    }
  }

  const ctaLabel = subscribed ? "Switch to this plan" : "Subscribe";

  return (
    <div>
      <PageHeader
        title="Plans & billing"
        description="Change your plan, switch monthly/annual, or redeem a code. Upgrades apply immediately; downgrades at the end of your billing period."
        action={
          isOwner && subscribed ? (
            <Button variant="outline" onClick={() => void manageSubscription()} disabled={managing}>
              {managing ? <Spinner /> : null} Manage subscription
            </Button>
          ) : undefined
        }
      />

      {!isOwner && (
        <p className="mb-6 text-sm text-muted-foreground">Only the workspace owner can change the plan.</p>
      )}
      {err && <p className="mb-4 text-sm text-destructive">{err}</p>}

      {plans === null ? (
        <div className="flex justify-center py-20">
          <Spinner />
        </div>
      ) : (
        <>
          {/* Monthly / Annual toggle */}
          <div className="mb-8 flex justify-center">
            <div className="inline-flex items-center rounded-full border bg-muted p-1 text-sm">
              <button
                type="button"
                onClick={() => setCycle("month")}
                className={
                  "rounded-full px-4 py-1.5 font-medium transition-colors " +
                  (cycle === "month" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground")
                }
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setCycle("year")}
                className={
                  "flex items-center gap-1.5 rounded-full px-4 py-1.5 font-medium transition-colors " +
                  (cycle === "year" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground")
                }
              >
                Annual
                <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-xs font-semibold text-primary">
                  Save 20%
                </span>
              </button>
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            {plans
              .filter((p) => !p.custom)
              .map((p) => (
                <PlanCard
                  key={p.id}
                  plan={p}
                  annual={annual}
                  current={!!acct && acct.plan === p.id}
                  busy={busyPlan === p.id}
                  canBuy={isOwner}
                  ctaLabel={ctaLabel}
                  onChoose={(credits) => void choose(p.id, credits)}
                />
              ))}
          </div>

          {plans.some((p) => p.custom) && (
            <div className="mt-6 flex flex-col items-start justify-between gap-4 rounded-xl border bg-card p-6 sm:flex-row sm:items-center">
              <div>
                <h3 className="text-lg font-semibold">Enterprise</h3>
                <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                  Custom credit volume, unlimited seats &amp; integrations, advanced security, and dedicated
                  support &amp; onboarding — tailored to your organization.
                </p>
              </div>
              <Button asChild variant="outline" className="shrink-0">
                <a href="mailto:sales@codebridge.ffmux.com?subject=CodeBridge%20Enterprise">
                  Contact sales <ArrowRight className="size-4" />
                </a>
              </Button>
            </div>
          )}

          {isOwner && (
            <div className="mt-8 text-center text-sm text-muted-foreground">
              <button
                type="button"
                className="underline underline-offset-4 hover:text-foreground"
                onClick={() => {
                  setRedeemErr("");
                  setRedeemOpen(true);
                }}
              >
                Have a promo code?
              </button>
            </div>
          )}
        </>
      )}

      <Dialog open={redeemOpen} onOpenChange={(o) => !redeeming && setRedeemOpen(o)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Redeem a promo code</DialogTitle>
            <DialogDescription>Enter a code to apply it to this workspace.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void applyCode();
            }}
            className="space-y-3"
          >
            <Input
              autoFocus
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setRedeemErr("");
              }}
              placeholder="Enter code"
              autoCapitalize="characters"
              className="uppercase"
              aria-invalid={!!redeemErr}
            />
            {redeemErr && <p className="text-sm text-destructive">{redeemErr}</p>}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setRedeemOpen(false)} disabled={redeeming}>
                Cancel
              </Button>
              <Button type="submit" disabled={!code.trim() || redeeming}>
                {redeeming ? <Spinner /> : null} Apply
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
